/**
 * Care Package: a reading done FOR someone, sent to them.
 * Spec: https://claude.ai/artifact/JgiptzEg41x3wAVTS5Gfjr
 *
 * The rules the client decides on its own. Everything about who the recipient
 * is (member or not) stays on the server; the client only ever asks for a
 * contact when a name was typed rather than picked.
 */

/** The ◉ for me · ♡ for someone toggle: top centre of the chat, at the start of
 *  the interview only, gone after the first exchange. */
const INTERVIEW_STATES = new Set(['INTERVIEW', 'TAROT_FUNNEL'])   // same pair draw-cards-offer knows
export function intentToggleVisible({ state, seekerTurns = 0, readingActive = false } = {}) {
  return INTERVIEW_STATES.has(state) && seekerTurns === 0 && !readingActive
}

/** Friends that match what was typed. Empty means: what you typed is the name. */
export function friendMatches(friends, query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return []
  return (friends || []).filter(f => (f.nick_name || '').toLowerCase().includes(q))
}

/** Who to offer as you type: friends first, then the people you have already
 * sent a care package to (by name; they may never have become members). A
 * name that is both appears once, as the friend. */
export function intentSuggestions(friends, sent, query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return []
  const matches = friendMatches(friends, query)
  const seen = new Set(matches.map(f => (f.nick_name || '').toLowerCase()))
  for (const s of (sent || [])) {
    const name = ((s && s.for_name) || '').trim()
    const key = name.toLowerCase()
    if (!name || seen.has(key) || !key.includes(q)) continue
    seen.add(key)
    matches.push({ querent_uuid: '', nick_name: name, past: true })
  }
  return matches
}

/** What the share screen asks for. null = not a gift, the ordinary screen. */
export function giftShareMode(gift) {
  if (!gift || !gift.is_gift) return null
  const member = !!gift.for_querent
  return { contact: !member, alias: !member }
}

/** The post-reading button for a gift. Before sending it is the way to the
 * share screen; once sent it reads the package (the sender's own view of
 * the landing) and there is no second send. ``sendIt``: her reading for one
 * of the people in her life (Stage 4), "Send it to Mom". */
export function giftAction(gift, { sendIt = false } = {}) {
  if (!gift || !gift.is_gift) return null
  const name = gift.for_name || 'them'
  if (gift.sent && gift.invite) {
    const poss = name.endsWith('s') ? `${name}'` : `${name}'s`
    return {
      // The room's chip reopens the send screen on the same invite (R7.12,
      // 2026-10-21); the sent page stays the Journal's and Messages' (href).
      label: `Read ${poss} gift`, icon: gift.opened ? 'fa-envelope-open' : 'fa-envelope', open: 'share',
      href: `/?reading=${encodeURIComponent(gift.reading_session_id || '')}&inv=${encodeURIComponent(gift.invite)}`,
    }
  }
  // Stage 4 (recipient journey R7.5): her reading for someone ends on one
  // chip, "Send it to Mom".
  if (sendIt) return { label: `Send it to ${name}`, icon: 'fa-gift', open: 'share' }
  return { label: `Gift to ${name}`, icon: 'fa-gift', open: 'share' }
}

// ── A send that did not go ──────────────────────────────────────────────────
// The server names the reason; we decide what the seeker reads. For the three
// delivery reasons the server's own sentence is the clearest thing we have, so
// it goes straight through. "self" is ours, because it is not an error so much
// as a nudge back to a reading you can simply do for yourself.
const SEND_REASONS = new Set(['self', 'sms_unavailable', 'invalid_contact', 'delivery_failed', 'opted_out'])
const SEND_FALLBACK = {
  self: "That's you. A care package is for someone else; you can always do a reading for yourself.",
  sms_unavailable: 'Email delivery only for now. Add their email and we will send it there.',
  invalid_contact: 'That does not look like a way to reach them. Check it and try again.',
  delivery_failed: 'It did not go through on their end. Try again in a moment.',
  // Recipient journey R10.8: the address asked us not to send.
  opted_out: 'This address has asked us not to send. Nothing will go out from here.',
}

const SEND_DEFAULT = "It didn't send. Try again."

/** The reason a send failed, as a status the share screen can hold.
 *  Anything we do not recognise is a plain 'error'. */
export function sendErrorReason(err) {
  const reason = err && err.response && err.response.data && err.response.data.reason
  return SEND_REASONS.has(reason) ? reason : 'error'
}

/** What to say under the button when a send failed. */
export function sendErrorMessage(err) {
  const reason = sendErrorReason(err)
  if (reason === 'error') return SEND_DEFAULT
  if (reason === 'self') return SEND_FALLBACK.self
  const said = err && err.response && err.response.data && err.response.data.error
  return (typeof said === 'string' && said.trim()) ? said.trim() : SEND_FALLBACK[reason]
}

/**
 * A send short of carats (a 402): how many MORE they need, in plain words
 * (e2e round 2, 2026-10-30). Before carats are shown the server sends words
 * in place of numbers (`short_of_carats`), and the Quest is the way on.
 * Null for anything that is not a 402.
 */
export function sendShortOf(err) {
  const r = err && err.response
  if (!r || r.status !== 402) return null
  const d = r.data || {}
  const visible = d.carats_visible !== false
  const required = Number(d.amount_required)
  const balance = Number(d.carat_balance !== undefined ? d.carat_balance : d.balance)
  if (!visible || !Number.isFinite(required) || !required) {
    const words = typeof d.words === 'string' && d.words.trim() ? d.words.trim() : ''
    if (!visible) return { need: 0, line: words || 'Quest shows what opens next.', visible: false }
    return { need: 0, line: 'You need more carats to send this.', visible: true }
  }
  const need = Math.ceil(required - (Number.isFinite(balance) ? balance : 0))
  if (need <= 0) return { need: 0, line: 'You need more carats to send this.', visible: true }
  return { need, line: `You need ${need.toLocaleString('en-US')} more carat${need === 1 ? '' : 's'} to send this.`, visible: true }
}

/**
 * A reading for someone asks its first question about that person (R3.3).
 * Ask it now when the entry held it for the name, or when the name moved
 * after it was asked but before any answer (the question was about the
 * wrong person). Never once she has answered.
 */
export function shouldAskOpening({ held = false, state, seekerTurns = 0 } = {}) {
  if (seekerTurns > 0) return false
  return !!held || INTERVIEW_STATES.has(state)
}

/** The chat without the opening question, which is about to be asked again. */
export function withoutOpeningQuestion(messages) {
  return (messages || []).filter((m) => !(m && m._context === 'welcome-question'))
}
